/**
 * R268ww · Feb 2026 — Interactive GSTR Report Preview.
 *
 * Renders one of the five GST reports (GSTR-1 / GSTR-3B / TDS Recv /
 * TDS Pay / ITC) as an Excel-like grid with:
 *   • Presets (This month, Last month, This quarter, This FY, Custom)
 *   • Custom start/end date range override (any range, not just YYYY-MM)
 *   • Global search across all visible columns
 *   • Per-column filter chips + sort (asc/desc/off)
 *   • Downloads (XLSX / CSV / JSON) that RESPECT the current filters
 *     plus a "Full period" secondary option for the raw dump.
 *
 * Backend contract:
 *   GET /gst/workspaces/{ws}/reports/{report}?period=YYYY-MM
 *                                          &start_date=YYYY-MM-DD
 *                                          &end_date=YYYY-MM-DD
 *                                          &format=json|xlsx|csv
 * The backend accepts EITHER `period` OR `start_date+end_date`; when
 * both are supplied, the explicit range wins.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Loader2, Download, Filter, X, ChevronUp, ChevronDown, ChevronsUpDown, Search } from "lucide-react";

const REPORT_META = {
  gstr1:            { path: "gstr-1",       label: "GSTR-1 · Outward supplies" },
  gstr3b:           { path: "gstr-3b",      label: "GSTR-3B · Monthly liability" },
  "tds-receivable": { path: "tds-register", label: "TDS · Receivable", side: "receivable" },
  "tds-payable":    { path: "tds-register", label: "TDS · Payable",    side: "payable" },
  itc:              { path: "itc-summary",  label: "ITC summary" },
};

const PRESET_OPTIONS = [
  { value: "this_month",   label: "This month" },
  { value: "last_month",   label: "Last month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "this_fy",      label: "This financial year (Apr–Mar)" },
  { value: "custom",       label: "Custom range…" },
];

/** Compute {start, end} ISO dates for a preset key. */
function resolvePreset(preset) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth(); // 0-indexed
  const iso = (d) => d.toISOString().slice(0, 10);
  if (preset === "this_month") {
    return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) };
  }
  if (preset === "last_month") {
    return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) };
  }
  if (preset === "this_quarter") {
    const q = Math.floor(m / 3);
    return { start: iso(new Date(y, q * 3, 1)), end: iso(new Date(y, q * 3 + 3, 0)) };
  }
  if (preset === "this_fy") {
    // Indian FY: Apr 1 → Mar 31
    const fyStartYear = m >= 3 ? y : y - 1;
    return { start: `${fyStartYear}-04-01`, end: `${fyStartYear + 1}-03-31` };
  }
  return null; // custom
}

/** Flatten a JSON report payload into a list of {section, ...row} rows
 *  with a stable column order. Handles all 5 report shapes. */
function normalizeReport(reportKey, payload) {
  if (!payload) return { columns: [], rows: [], summary: null };
  if (reportKey === "gstr1") {
    const p = payload.gstr1 || {};
    const cols = [
      { key: "section",         label: "Section" },
      { key: "doc_date",        label: "Date" },
      { key: "doc_no",          label: "Doc No" },
      { key: "customer_name",   label: "Customer" },
      { key: "customer_gstin",  label: "GSTIN" },
      { key: "place_of_supply", label: "POS" },
      { key: "taxable",         label: "Taxable", numeric: true },
      { key: "igst",            label: "IGST", numeric: true },
      { key: "cgst",            label: "CGST", numeric: true },
      { key: "sgst",            label: "SGST", numeric: true },
      { key: "cess",            label: "Cess", numeric: true },
      { key: "total",           label: "Total", numeric: true },
    ];
    const rows = [
      ...(p.b2b || []).map((r) => ({ ...r, section: "B2B" })),
      ...(p.b2cs || []).map((r) => ({ ...r, section: "B2CS" })),
      // CDNR values are negatives against the source invoice
      ...(p.cdnr || []).map((r) => ({ ...r, section: "CDNR",
        taxable: -r.taxable, igst: -r.igst, cgst: -r.cgst,
        sgst: -r.sgst, cess: -r.cess, total: -r.total })),
    ];
    return { columns: cols, rows, summary: p.summary };
  }
  if (reportKey === "gstr3b") {
    const p = payload.gstr3b || {};
    const cols = [
      { key: "section", label: "Section" },
      { key: "taxable", label: "Taxable", numeric: true },
      { key: "igst",    label: "IGST", numeric: true },
      { key: "cgst",    label: "CGST", numeric: true },
      { key: "sgst",    label: "SGST", numeric: true },
      { key: "cess",    label: "Cess", numeric: true },
    ];
    const rows = [
      { section: "3.1(a) Outward taxable (Sales − CN)", ...p.section_3_1a_outward_taxable },
      { section: "4A ITC available", ...p.section_4_itc_available },
      { section: "4B ITC reversed",  ...p.section_4b_itc_reversed },
      { section: "4C ITC claimed (informational)", ...p.section_4c_itc_claimed },
      { section: "NET LIABILITY", ...p.net_liability, taxable: "" },
    ];
    return { columns: cols, rows, summary: null };
  }
  if (reportKey === "tds-receivable" || reportKey === "tds-payable") {
    const p = payload.tds || {};
    const cols = [
      { key: "Date",        label: "Date" },
      { key: "Doc No",      label: "Doc No" },
      { key: "Party",       label: "Party" },
      { key: "GSTIN",       label: "GSTIN" },
      { key: "Section",     label: "Section" },
      { key: "Rate %",      label: "Rate %", numeric: true },
      { key: "Taxable",     label: "Taxable", numeric: true },
      { key: "TDS amount",  label: "TDS amount", numeric: true },
    ];
    return { columns: cols, rows: p.rows || [],
             summary: { total_tds: p.total_tds } };
  }
  if (reportKey === "itc") {
    const p = payload.itc || {};
    const cols = [
      { key: "period",        label: "Period" },
      { key: "kind",          label: "Kind" },
      { key: "source_bill_id",label: "Source" },
      { key: "amount_igst",   label: "IGST", numeric: true },
      { key: "amount_cgst",   label: "CGST", numeric: true },
      { key: "amount_sgst",   label: "SGST", numeric: true },
      { key: "amount_ugst",   label: "UGST", numeric: true },
      { key: "amount_cess",   label: "Cess", numeric: true },
      { key: "notes",         label: "Notes" },
    ];
    return { columns: cols, rows: p.rows || [], summary: { balance: p.balance } };
  }
  return { columns: [], rows: [], summary: null };
}

function fmtCell(v, col) {
  if (v === null || v === undefined || v === "") return "";
  if (col.numeric) {
    const n = Number(v);
    if (!isFinite(n)) return String(v);
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(v);
}

export default function GstReportPreview({ ws, reportKey }) {
  const meta = REPORT_META[reportKey];
  const [preset, setPreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [filters, setFilters] = useState({}); // key -> Set<string> of allowed values

  // Compute the range from preset (or custom fields).
  const range = useMemo(() => {
    if (preset === "custom") {
      return customStart && customEnd ? { start: customStart, end: customEnd } : null;
    }
    return resolvePreset(preset);
  }, [preset, customStart, customEnd]);

  const rangeLabel = useMemo(() => {
    if (!range) return "";
    if (range.start.slice(0, 7) === range.end.slice(0, 7)
        && range.start.slice(-2) === "01"
        && parseInt(range.end.slice(-2), 10) >= 28) {
      return range.start.slice(0, 7);
    }
    return `${range.start} → ${range.end}`;
  }, [range]);

  const buildQuery = useCallback((format = "json") => {
    if (!range) return null;
    const params = new URLSearchParams();
    params.set("start_date", range.start);
    params.set("end_date",   range.end);
    // Some endpoints (ITC) don't accept start/end yet, so keep period
    // as well for backward compatibility when the range fits one month.
    if (range.start.slice(0, 7) === range.end.slice(0, 7)) {
      params.set("period", range.start.slice(0, 7));
    }
    if (meta.side) params.set("side", meta.side);
    params.set("format", format);
    return params.toString();
  }, [range, meta]);

  const runPreview = useCallback(async () => {
    const q = buildQuery("json");
    if (!q) return;
    setLoading(true); setFilters({}); setSort({ key: null, dir: "asc" });
    try {
      const r = await api.get(`/gst/workspaces/${ws}/reports/${meta.path}?${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load report"));
      setData(null);
    } finally { setLoading(false); }
  }, [buildQuery, ws, meta.path]);

  useEffect(() => { runPreview(); /* eslint-disable-next-line */ }, [reportKey]);

  const { columns, rows: rawRows, summary } = useMemo(
    () => normalizeReport(reportKey, data), [reportKey, data]);

  // Distinct values per column (for filter chips)
  const distinct = useMemo(() => {
    const m = {};
    columns.forEach((c) => {
      const vals = new Set();
      rawRows.forEach((r) => {
        const v = r[c.key];
        if (v !== null && v !== undefined && v !== "") vals.add(String(v));
      });
      m[c.key] = Array.from(vals).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    });
    return m;
  }, [columns, rawRows]);

  // Apply search + filters + sort
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const active = Object.keys(filters).filter((k) => filters[k] && filters[k].size > 0);
    let out = rawRows.filter((r) => {
      if (q) {
        const hit = columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q));
        if (!hit) return false;
      }
      for (const k of active) {
        const set = filters[k];
        if (!set.has(String(r[k] ?? ""))) return false;
      }
      return true;
    });
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      const mul = sort.dir === "desc" ? -1 : 1;
      out = out.slice().sort((a, b) => {
        const av = a[sort.key]; const bv = b[sort.key];
        if (col && col.numeric) return (Number(av || 0) - Number(bv || 0)) * mul;
        return String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true }) * mul;
      });
    }
    return out;
  }, [rawRows, search, filters, sort, columns]);

  const totals = useMemo(() => {
    const t = {};
    columns.forEach((c) => {
      if (!c.numeric) return;
      t[c.key] = rows.reduce((sum, r) => sum + (Number(r[c.key]) || 0), 0);
    });
    return t;
  }, [columns, rows]);

  const toggleSort = (k) => {
    setSort((s) => {
      if (s.key !== k) return { key: k, dir: "asc" };
      if (s.dir === "asc") return { key: k, dir: "desc" };
      return { key: null, dir: "asc" };
    });
  };

  const toggleFilterVal = (colKey, v) => {
    setFilters((f) => {
      const next = { ...f };
      const cur = new Set(next[colKey] || []);
      if (cur.has(v)) cur.delete(v); else cur.add(v);
      if (cur.size === 0) delete next[colKey]; else next[colKey] = cur;
      return next;
    });
  };
  const clearFilter = (colKey) => setFilters((f) => { const n = { ...f }; delete n[colKey]; return n; });

  const activeFilterCount = Object.keys(filters).length;

  const downloadFiltered = async (format) => {
    if (!rows.length) { toast.error("No rows to download"); return; }
    try {
      if (format === "json") {
        const blob = new Blob([JSON.stringify({ columns, rows, totals, summary }, null, 2)],
                              { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${reportKey}-filtered-${rangeLabel || "range"}.json`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("Downloaded (filtered)");
        return;
      }
      // XLSX / CSV: build client-side from `rows`.
      const header = columns.map((c) => c.label);
      const dataRows = rows.map((r) => columns.map((c) => (r[c.key] ?? "")));
      const totalRow = columns.map((c) => c.numeric ? Number(totals[c.key] || 0).toFixed(2)
                                                     : (c.key === "section" || c.key === "doc_no" ? "TOTAL" : ""));
      if (format === "csv") {
        const csv = [header, ...dataRows, totalRow].map((r) =>
          r.map((v) => {
            const s = String(v ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          }).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${reportKey}-filtered-${rangeLabel || "range"}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("Downloaded (filtered)");
        return;
      }
      // XLSX — use SheetJS `xlsx` package (already in node_modules).
      const XLSX = await import("xlsx").catch(() => null);
      if (!XLSX) {
        toast.error("Excel library unavailable — try CSV");
        return;
      }
      const aoa = [header, ...dataRows, totalRow];
      const ws2 = XLSX.utils.aoa_to_sheet(aoa);
      // Auto column widths.
      ws2["!cols"] = header.map((h, i) => {
        const maxLen = Math.max(String(h).length,
          ...dataRows.map((r) => String(r[i] ?? "").length));
        return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws2, meta.label.slice(0, 31));
      const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${reportKey}-filtered-${rangeLabel || "range"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Downloaded (filtered)");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Download failed"));
    }
  };

  const downloadFullPeriod = async (format) => {
    const q = buildQuery(format);
    if (!q) return;
    try {
      const r = await api.get(`/gst/workspaces/${ws}/reports/${meta.path}?${q}`,
                              { responseType: "blob" });
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = format === "xlsx" ? "xlsx" : format === "csv" ? "csv" : "json";
      a.href = url; a.download = `${reportKey}-full-${rangeLabel || "range"}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Downloaded (full period)");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Download failed"));
    }
  };

  return (
    <div className="space-y-3" data-testid={`gst-preview-${reportKey}`}>
      {/* Header: preset + custom range + Apply */}
      <div className="border border-border p-3 space-y-2 bg-muted/10 rounded-none" data-testid="gst-preview-filters">
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Range</Label>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="h-9 w-52 rounded-none" data-testid="gst-preview-preset">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESET_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === "custom" && (
            <>
              <Input type="date" value={customStart}
                     onChange={(e) => setCustomStart(e.target.value)}
                     className="h-9 w-40 rounded-none"
                     data-testid="gst-preview-start" />
              <span className="text-xs text-muted-foreground">→</span>
              <Input type="date" value={customEnd}
                     onChange={(e) => setCustomEnd(e.target.value)}
                     className="h-9 w-40 rounded-none"
                     data-testid="gst-preview-end" />
            </>
          )}
          {range && (
            <span className="text-[11px] text-muted-foreground font-mono">
              {range.start} → {range.end}
            </span>
          )}
          <Button size="sm" className="h-9 rounded-none ml-auto"
                  onClick={runPreview} disabled={loading || !range}
                  data-testid="gst-preview-apply">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                     : <Filter className="w-3.5 h-3.5 mr-1.5" />}
            Apply
          </Button>
        </div>

        {/* Row: search + downloads */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/60">
          <Search className="w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Search across all columns…"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 className="h-8 w-64 rounded-none text-xs"
                 data-testid="gst-preview-search" />
          {activeFilterCount > 0 && (
            <Button size="sm" variant="outline" className="h-7 rounded-none text-[11px]"
                    onClick={() => setFilters({})}
                    data-testid="gst-preview-clear-filters">
              Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
              <X className="w-3 h-3 ml-1" />
            </Button>
          )}
          <span className="text-[11px] text-muted-foreground ml-1">
            {rows.length} / {rawRows.length} rows
          </span>
          <div className="ml-auto flex items-center gap-1">
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground mr-1">Filtered:</span>
            {["xlsx", "csv", "json"].map((f) => (
              <Button key={f} size="sm" variant="outline" className="h-7 rounded-none text-[11px]"
                      onClick={() => downloadFiltered(f)}
                      data-testid={`gst-preview-dl-filtered-${f}`}>
                <Download className="w-3 h-3 mr-1" />
                {f.toUpperCase()}
              </Button>
            ))}
            <span className="mx-1 text-muted-foreground/40">|</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="ghost" className="h-7 rounded-none text-[11px]"
                        data-testid="gst-preview-dl-full-menu">
                  Full period ▾
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-40 p-1 rounded-none">
                {["xlsx", "csv", "json"].map((f) => (
                  <button key={f} className="w-full text-left px-2 py-1 text-xs hover:bg-muted"
                          onClick={() => downloadFullPeriod(f)}
                          data-testid={`gst-preview-dl-full-${f}`}>
                    <Download className="w-3 h-3 mr-1 inline" /> {f.toUpperCase()}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Summary block (if present) */}
      {summary && (
        <div className="border border-border p-2 flex flex-wrap gap-3 text-[11px]"
             data-testid="gst-preview-summary">
          {Object.entries(summary).map(([k, v]) => {
            if (v === null || v === undefined) return null;
            if (typeof v === "object") {
              return (
                <div key={k}>
                  <span className="uppercase tracking-widest text-[9px] text-muted-foreground mr-1">{k.replace(/_/g, " ")}:</span>
                  <span className="font-mono">{Object.entries(v).map(([kk, vv]) => `${kk}: ${vv}`).join(" · ")}</span>
                </div>
              );
            }
            return (
              <div key={k}>
                <span className="uppercase tracking-widest text-[9px] text-muted-foreground mr-1">{k.replace(/_/g, " ")}:</span>
                <span className="font-mono">{typeof v === "number" ? v.toLocaleString() : String(v)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Grid */}
      <div className="border border-border overflow-auto max-h-[65vh]"
           data-testid="gst-preview-grid">
        {loading && !data ? (
          <div className="p-6 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading report…
          </div>
        ) : !rawRows.length ? (
          <div className="p-6 text-xs text-muted-foreground">
            No data for this range. Try widening the date range.
          </div>
        ) : (
          <table className="w-full text-xs border-collapse" data-testid="gst-preview-table">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                {columns.map((c) => {
                  const isSorted = sort.key === c.key;
                  const activeFilter = !!filters[c.key];
                  return (
                    <th key={c.key}
                        className={"text-[10px] uppercase tracking-widest text-muted-foreground font-semibold px-2 py-2 border border-border whitespace-nowrap select-none "
                          + (c.numeric ? "text-right" : "text-left") + (activeFilter ? " bg-primary/10" : "")}
                        data-testid={`gst-preview-th-${c.key}`}>
                      <div className={"flex items-center gap-1 " + (c.numeric ? "justify-end" : "")}>
                        <button className="hover:text-foreground flex items-center gap-1"
                                onClick={() => toggleSort(c.key)}
                                data-testid={`gst-preview-sort-${c.key}`}>
                          <span>{c.label}</span>
                          {isSorted && sort.dir === "asc" && <ChevronUp className="w-3 h-3" />}
                          {isSorted && sort.dir === "desc" && <ChevronDown className="w-3 h-3" />}
                          {!isSorted && <ChevronsUpDown className="w-3 h-3 opacity-40" />}
                        </button>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className={"p-0.5 rounded-sm hover:bg-primary/20 " + (activeFilter ? "text-primary" : "text-muted-foreground/60")}
                                    data-testid={`gst-preview-filter-${c.key}`}
                                    title="Filter">
                              <Filter className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-56 p-2 rounded-none max-h-72 overflow-auto">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Filter · {c.label}</span>
                              {activeFilter && (
                                <button className="text-[10px] text-primary hover:underline"
                                        onClick={() => clearFilter(c.key)}
                                        data-testid={`gst-preview-filter-clear-${c.key}`}>Clear</button>
                              )}
                            </div>
                            {distinct[c.key].length === 0 && (
                              <div className="text-[10px] text-muted-foreground italic py-2">No distinct values</div>
                            )}
                            <ul className="space-y-0.5">
                              {distinct[c.key].map((v) => {
                                const checked = filters[c.key]?.has(v);
                                return (
                                  <li key={v}>
                                    <label className="flex items-center gap-2 text-[11px] cursor-pointer hover:bg-muted px-1 py-0.5">
                                      <input type="checkbox"
                                             checked={!!checked}
                                             onChange={() => toggleFilterVal(c.key, v)}
                                             data-testid={`gst-preview-filter-opt-${c.key}-${v}`} />
                                      <span className="truncate flex-1">{v}</span>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-primary/5" data-testid={`gst-preview-row-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key}
                        className={"px-2 py-1 border border-border/60 whitespace-nowrap "
                          + (c.numeric ? "text-right font-mono" : "")}
                        title={String(r[c.key] ?? "")}>
                      {fmtCell(r[c.key], c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {/* Totals footer (numeric columns only) */}
            {columns.some((c) => c.numeric) && rows.length > 0 && (
              <tfoot className="sticky bottom-0 bg-muted">
                <tr>
                  {columns.map((c, i) => (
                    <td key={c.key}
                        className={"px-2 py-1.5 border border-border font-bold text-[11px] "
                          + (c.numeric ? "text-right font-mono" : "")}
                        data-testid={`gst-preview-total-${c.key}`}>
                      {i === 0 ? "TOTAL" : (c.numeric ? Number(totals[c.key] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "")}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}
